import { useTranslation } from 'react-i18next';

import {
  fromDisplayQty,
  getUnitLabel,
  supportsHandlingUnits,
  toDisplayQty,
  type BundleConfig
} from '../lib/quantities';
import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';
import type { ConfirmArrivalUnitMode } from '../model/types';
import { ConfirmArrivalInventoryQty } from './confirm-arrival-inventory-qty';
import { ConfirmArrivalQuantityInput } from './confirm-arrival-quantity-input';

interface ConfirmArrivalQuantityCellProps {
  /** Unique per cell; the second input's id derives from it. */
  id: string;
  /** `<line reference> — <column>`, the accessible name of the primary input. */
  label: string;
  line: BundleConfig;
  unitMode: ConfirmArrivalUnitMode;
  /** Inventory units — what the form holds. */
  value: number;
  /** Inventory units. */
  ceiling: number;
  /** Receives inventory units, already converted from whichever input was typed in. */
  onChange: (inventoryQty: number) => void;
  secondaryOpen: boolean;
  onToggleSecondary: () => void;
  error?: string;
}

/**
 * One quantity with its unit calculator: the input in the table's unit, the
 * same quantity in the other unit on demand, and — while handling units show —
 * the inventory figure the API will actually receive.
 *
 * Shared by the receipt and the release tables, so both convert and round alike.
 */
export function ConfirmArrivalQuantityCell({
  id,
  label,
  line,
  unitMode,
  value,
  ceiling,
  onChange,
  secondaryOpen: isSecondaryRequested,
  onToggleSecondary,
  error
}: ConfirmArrivalQuantityCellProps) {
  const { t } = useTranslation();

  const hasHandlingUnits = supportsHandlingUnits(line);
  const isHandlingMode = unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING;
  const otherMode = isHandlingMode ? CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY : CONFIRM_ARRIVAL_UNIT_MODE.HANDLING;
  const unitLabel = getUnitLabel(line, unitMode);
  const otherUnitLabel = getUnitLabel(line, otherMode);
  const secondaryOpen = hasHandlingUnits && isSecondaryRequested;
  const secondaryId = `${id}-secondary`;

  return (
    <>
      <ConfirmArrivalQuantityInput
        id={id}
        aria-label={label}
        value={toDisplayQty(line, unitMode, value)}
        max={toDisplayQty(line, unitMode, ceiling)}
        onChange={(next) => onChange(fromDisplayQty(line, unitMode, next))}
        unitLabel={unitLabel}
        // Lines without a usable bundle configuration keep a plain label.
        unitToggle={
          hasHandlingUnits
            ? {
                label: t('receive.confirm.alsoInUnit', 'Also in {{unit}}', { unit: otherUnitLabel }),
                expanded: secondaryOpen,
                controls: secondaryId,
                onToggle: onToggleSecondary
              }
            : undefined
        }
        invalid={Boolean(error)}
      />
      {secondaryOpen && (
        <ConfirmArrivalQuantityInput
          id={secondaryId}
          className="mt-1.5"
          aria-label={`${label} (${otherUnitLabel})`}
          value={toDisplayQty(line, otherMode, value)}
          max={toDisplayQty(line, otherMode, ceiling)}
          onChange={(next) => onChange(fromDisplayQty(line, otherMode, next))}
          unitLabel={otherUnitLabel}
          invalid={Boolean(error)}
        />
      )}
      {/* An open second input already carries the inventory figure. */}
      {isHandlingMode && hasHandlingUnits && !secondaryOpen && (
        <ConfirmArrivalInventoryQty className="mt-1" quantity={value} unitLabel={line.ltuTypeDescription} />
      )}
      {error && <p className="mt-1 max-w-50 text-xs text-destructive">{error}</p>}
    </>
  );
}
